import { useEffect, useRef, useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { ArrowUpCircle, Loader2 } from 'lucide-react'
import { useRestartBlocker } from '@/lib/restartGuard'
import { applyWebUpdate, useWebUpdate } from '@/lib/webUpdate'
import { autoApplyAllowed, isListeningScreen, manualApplyBlocker } from '@/lib/webUpdatePolicy'

/** Must match `h-10` below; shares the desktop banner's layout slot. */
const BANNER_HEIGHT = '2.5rem'
const EVALUATE_EVERY_MS = 15_000

/**
 * "New version" strip for browser and Bhookly Server APK tabs (F-FE-N191) —
 * the web counterpart of `DesktopUpdateBanner`, which it never appears beside
 * (Electron never starts `webUpdate.ts`, so `mode` stays null there).
 *
 * Applies itself once the screen has been left alone for a couple of minutes
 * with no open checkout (`restartGuard`), nothing saving and a live
 * connection. The counter draft survives a reload (`useCounterDraft`), so an
 * idle cart does not hold an update back. Listening screens (KDS, online
 * orders) never reload by themselves — see `LISTENING_SCREENS`.
 *
 * Publishes its height as `--desktop-update-banner-h` so the full-height shells
 * make room for it exactly as they do for the desktop strip.
 */
export function WebUpdateBanner() {
  const { mode, remoteCommit } = useWebUpdate()
  const blocker = useRestartBlocker()
  const mutating = useIsMutating() > 0
  const [applying, setApplying] = useState(false)
  const lastInputAt = useRef(Date.now())

  const visible = mode !== null

  useEffect(() => {
    if (!visible) return
    const root = document.documentElement
    root.style.setProperty('--desktop-update-banner-h', BANNER_HEIGHT)
    return () => {
      root.style.removeProperty('--desktop-update-banner-h')
    }
  }, [visible])

  useEffect(() => {
    const touch = () => {
      lastInputAt.current = Date.now()
    }
    const opts: AddEventListenerOptions = { passive: true, capture: true }
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const
    for (const e of events) document.addEventListener(e, touch, opts)
    return () => {
      for (const e of events) document.removeEventListener(e, touch, opts)
    }
  }, [])

  // Latest values for the interval without re-arming it on every render.
  const live = useRef({ mode, blocker, mutating, applying })
  live.current = { mode, blocker, mutating, applying }

  useEffect(() => {
    if (!visible) return
    const id = window.setInterval(() => {
      const s = live.current
      if (s.applying || s.mode === null) return
      const ok = autoApplyAllowed({
        ready: true,
        blocker: s.blocker,
        mutating: s.mutating,
        idleMs: Date.now() - lastInputAt.current,
        online: navigator.onLine !== false,
        pathname: window.location.pathname,
      })
      if (!ok) return
      setApplying(true)
      void applyWebUpdate(s.mode).catch(() => setApplying(false))
    }, EVALUATE_EVERY_MS)
    return () => window.clearInterval(id)
  }, [visible])

  if (!mode) return null

  const reason = manualApplyBlocker({
    blocker,
    mutating,
    online: typeof navigator === 'undefined' || navigator.onLine !== false,
  })
  const listening = isListeningScreen(window.location.pathname)

  const onApply = () => {
    if (reason || applying) return
    setApplying(true)
    void applyWebUpdate(mode).catch(() => setApplying(false))
  }

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-[80] flex h-10 items-center justify-center gap-3 border-b border-emerald-800/40 bg-emerald-600 px-3 text-white shadow-sm"
    >
      <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate text-sm font-medium">
        New version ready
        {remoteCommit ? <span className="opacity-80"> · {remoteCommit}</span> : null}
      </span>

      {reason ? (
        <span className="hidden truncate text-xs opacity-90 sm:inline">{reason}</span>
      ) : !listening ? (
        <span className="hidden truncate text-xs opacity-90 md:inline">Updates by itself when the screen is idle</span>
      ) : null}

      <button
        type="button"
        onClick={onApply}
        disabled={Boolean(reason) || applying}
        title={reason ?? 'Reloads the POS (a few seconds)'}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-card px-3 text-xs font-semibold text-success-fg shadow-sm transition-colors hover:bg-success-surface disabled:cursor-not-allowed disabled:opacity-50"
      >
        {applying ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Updating…
          </>
        ) : (
          'Update now'
        )}
      </button>
    </div>
  )
}
